import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Button, Loader, Modal } from "semantic-ui-react";

import { goToParent } from "@teamprevent/common/routing";

import {
    CreateUpdateOccupationalIllness,
    IsOccupationalIllnessValid,
    OccupationalIllness
} from "../models";
import { OhCaseListModel } from "../../crud/ohCase";

import { ohCaseApi } from "../../crud/ohCase";
import { employeeRecordIdSelector, customerIdSelector } from "../selectors";

import { OccupationalIllnessForm } from "./OccupationalIllnessForm";

interface Props {
    title: string;
    occupationalIllness: CreateUpdateOccupationalIllness;
    saveModel: (customerId: string, employeeRecordId: string, occupationalIllness: CreateUpdateOccupationalIllness) => Promise<OccupationalIllness>;
    onSaved: (occupationalIllness: OccupationalIllness) => void;
}

export const CreateEditModal: React.FunctionComponent<Props> = ({ title, onSaved, occupationalIllness, saveModel }: Props) => {
    const customerId = useSelector(customerIdSelector);
    const employeeRecordId = useSelector(employeeRecordIdSelector);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);

    const [model, setModel] = React.useState<CreateUpdateOccupationalIllness>(occupationalIllness);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [ohCases, setOhCases] = React.useState<OhCaseListModel[] | null>(null);

    const dispatch = useDispatch();
    const onClose = React.useCallback(() => dispatch(goToParent()), [dispatch]);

    React.useEffect(() => {
        const fetchData = async () => {
            const ohCaseList = await ohCaseApi.getAllForEmployeeRecord(customerId, employeeRecordId);
            setOhCases(ohCaseList);
        };
        setLoading(true);
        fetchData().then(() => setLoading(false));
    }, [customerId, employeeRecordId]);

    const onSave = React.useCallback(async () => {
        setSubmitted(true);
        if (!IsOccupationalIllnessValid(model)) {
            return;
        }
        setSaving(true);
        const createdOccupationalIllness = await saveModel(customerId, employeeRecordId, model);
        onSaved(createdOccupationalIllness);
        setSaving(false);
        onClose();
    }, [customerId, employeeRecordId, model, onClose, onSaved, saveModel]);

    if (loading || ohCases === null) {
        return <Loader />;
    }

    return (
        <Modal open size="small" closeOnDimmerClick={false}>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Content>
                <OccupationalIllnessForm
                    occupationalIllness={model}
                    onChange={setModel}
                    ohCases={ohCases}
                    showErrors={submitted}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Cancel</Button>
                <Button loading={saving} disabled={saving} onClick={onSave} content="Save" />
            </Modal.Actions>
        </Modal>
    );
};
